/**
 * Sanitizes HTML content by removing empty paragraph tags that only contain line breaks.
 * This prevents double spacing when content is rendered in emails or the application.
 * Called only at save time to avoid janky editor behavior during editing.
 *
 * @param html - The HTML string to sanitize
 * @returns Cleaned HTML string without empty <p><br></p> patterns
 */
export const sanitizeContentForSave = (html: string): string => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html || '', 'text/html')
    const body = doc.body

    /**
     * Helper function to normalize text content for comparison
     * - Replaces \u00A0 (non-breaking space/&nbsp;) with regular space
     * - Removes \u200B (zero-width space) characters
     * - Trims whitespace from both ends
     */
    const normalizedText = (node: Node) =>
      (node.textContent || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\u200B/g, '')
        .trim()

    // Remove all empty <p> elements that only contain <br> tags
    // This fixes the double line break issue when rendering in emails/apps
    const paragraphs = Array.from(body.querySelectorAll('p'))
    paragraphs.forEach((p) => {
      // Check if the paragraph has any actual text content
      const hasText = normalizedText(p) !== ''

      if (!hasText) {
        // Check if it only contains whitespace and/or <br> elements
        const childNodes = Array.from(p.childNodes)
        const onlyBrAndWhitespace = childNodes.every((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return normalizedText(node) === ''
          }
          if (node.nodeType === Node.ELEMENT_NODE) {
            return (node as Element).tagName.toLowerCase() === 'br'
          }
          return true
        })

        if (onlyBrAndWhitespace) {
          p.remove()
        }
      }
    })

    return body.innerHTML
  } catch (error) {
    console.error('🚨 sanitizeContentForSave: HTML parsing failed', error)
    return html || ''
  }
}

